import {useEffect, useMemo, useRef, useState} from 'react';
import {ConditionalStyles, TableStyles} from 'react-data-table-component';
import {toRem} from 'shared/helpers';
import {COLORS} from 'shared/consts';
import {HivesRow} from 'features/hives/ui/hivesTable/types';
import {useHivesStore} from 'entities/hives';
import {useNavigate} from 'react-router-dom';
import {useApiariesStore} from 'entities/apiaries';
import {useDevicesStore} from 'entities/devices';

export type HivesColumnSelector =
  | 'number'
  | 'year'
  | 'mark'
  | 'breed'
  | 'states'
  | 'frames'
  | 'strength'
  | 'brood'
  | 'happiness'
  | 'devices'
  | 'updatedAt';

export type HivesColItem = {name: string; id: HivesColumnSelector; isSelected: boolean};

const LS_KEY = 'hives_activeCols';

export const useHivesTable = () => {
  const navigate = useNavigate();

  const hives = useHivesStore((s) => s.hives);
  const devices = useDevicesStore((s) => s.devices);

  const isLoading = useHivesStore((s) => s.isLoading ?? false);
  const isEnd = useHivesStore((s) => s.isEnd);
  const totalCount = useHivesStore((s) => s.totalCount);

  const getHivesFromServer = useHivesStore((s) => s.getHivesFromServer);
  const getNextPage = useHivesStore((s) => s.getNextPage);

  const apiaryId = useApiariesStore((s) => s.currentApiaryId);

  const [activeCols, setActiveCols] = useState<HivesColumnSelector[]>([]);
  const [colItems, setColItems] = useState<HivesColItem[]>([
    {name: 'Номер улья', id: 'number', isSelected: true},
    {name: 'Год матки', id: 'year', isSelected: true},
    {name: 'Метка', id: 'mark', isSelected: true},
    {name: 'Порода', id: 'breed', isSelected: true},
    {name: 'Состояние', id: 'states', isSelected: true},
    {name: 'Рамки', id: 'frames', isSelected: true},
    {name: 'Сила', id: 'strength', isSelected: true},
    {name: 'Расплод', id: 'brood', isSelected: true},
    {name: 'Индекс счастья', id: 'happiness', isSelected: true},
    {name: 'Устройства', id: 'devices', isSelected: true},
    {name: 'Обновлено', id: 'updatedAt', isSelected: true},
  ]);

  // Форматируем только updatedAt для UI.
  const rows = useMemo<HivesRow[]>(() => {
    return (hives || []).map((r) => {
      const hiveDevices = devices.filter((d) => d.hiveId === r.id);

      console.log(hiveDevices);

      return {
        id: r.id,
        number: r.number,
        apiary: r.apiary,
        uterus: {
          color: r.uterus?.color ?? '',
          year: r.uterus?.year ?? null,
          mark: r.uterus?.mark ?? null,
          breed: r.uterus?.breed ?? null,
        },
        states: Array.isArray(r.states) ? r.states : [],
        frames: r.frames,
        strength: r.strength,
        brood: r.brood,
        devices: hiveDevices.length,
        happiness: r.happiness,
        isSmart: hiveDevices.length > 0,
        createdAt: r.createdAt,
        updatedAt: r.updatedAt ? new Date(r.updatedAt) : '',
        deletedAt: r.deletedAt ?? null,
      };
    });
  }, [hives]);

  const conditionalRowStyles: ConditionalStyles<HivesRow>[] = [
    {
      when: (row) => !!row.deletedAt,
      classNames: ['row--archived'],
      style: {
        cursor: 'not-allowed',
      },
    },
  ];

  const customStyles: TableStyles = useMemo(
    () => ({
      table: {style: {backgroundColor: 'transparent'}},
      headRow: {
        style: {
          position: 'sticky',
          top: 0,
          zIndex: 5,
          backgroundColor: COLORS.BG.SURFACE3,
          borderTop: `${toRem(1)} solid ${COLORS.BORDER.SOFT}`,
          borderBottom: `${toRem(1)} solid ${COLORS.BORDER.SOFT}`,
          minHeight: '40px',
          color: COLORS.FG.DEFAULT,
        },
      },
      rows: {
        style: {
          minHeight: '40px!important',
          borderBottom: `${toRem(1)} solid ${COLORS.BORDER.MUTED}`,
          cursor: 'pointer',
          backgroundColor: 'transparent',
          color: COLORS.FG.DEFAULT,
          '&:hover': {backgroundColor: COLORS.BG.SURFACE2},
        },
      },
      noData: {style: {backgroundColor: 'transparent', color: COLORS.FG.DEFAULT}},
    }),
    []
  );

  const onActiveColsChange = (items: HivesColItem[]) => {
    setColItems(items);
    const selected = items.filter((i) => i.isSelected).map((i) => i.id);
    setActiveCols(selected);
    localStorage.setItem(LS_KEY, JSON.stringify(selected));
  };

  const onRowClick = (row: HivesRow) => {
    // Архивированные ульи некликабельны
    if (row.deletedAt) return;
    navigate(`/hive/${row.id}`);
  };

  useEffect(() => {
    const storage = localStorage.getItem(LS_KEY);
    if (!storage) {
      const def: HivesColumnSelector[] = [
        'number',
        'year',
        'mark',
        'breed',
        'states',
        'frames',
        'strength',
        'brood',
        'happiness',
        'devices',
        'updatedAt',
      ];
      setActiveCols(def);
      localStorage.setItem(LS_KEY, JSON.stringify(def));
    } else {
      try {
        const arr: HivesColumnSelector[] = JSON.parse(storage);
        setActiveCols(arr);
        setColItems((prev) => prev.map((c) => ({...c, isSelected: arr.includes(c.id)})));
      } catch {
        localStorage.removeItem(LS_KEY);
      }
    }
  }, []);

  const loadingRef = useRef(false);

  useEffect(() => {
    if (!apiaryId) return;

    loadingRef.current = false;
    (async () => {
      try {
        await getHivesFromServer({apiaryId}, true);
      } catch {
        console.warn('error');
      }
    })();
  }, [apiaryId, getHivesFromServer]);

  const onInfiniteScroll = async (inView?: boolean) => {
    if (!inView) return;
    if (loadingRef.current) return;
    if (isLoading) return;
    if (!apiaryId) return;
    if (typeof totalCount === 'number' && rows.length >= totalCount) return;
    if (isEnd) return;

    loadingRef.current = true;
    try {
      await getNextPage(null);
    } finally {
      loadingRef.current = false;
    }
  };

  return {
    rows,
    isLoading,
    totalCount,
    conditionalRowStyles,
    onInfiniteScroll,
    customStyles,
    activeCols,
    colItems,
    onActiveColsChange,
    onRowClick,
  };
};
